// WHAT: Main screen component for Kanban board view of roadmap items
// WHY:  Provides status-based Kanban board layout grouped by modules
// DOMAIN: Admin → Roadmap → Kanban
// DEPENDENCIES: useFilteredRoadmap hook, useRoadmapContext, FilterBar, EditModal, ModuleManager
// RISKS:
//   - NOTE: Uses crypto.randomUUID() for new item IDs - ensure browser support
//   - NOTE: Items without module default to 'Other' - may cause confusion
//   - NOTE: Statistics calculated from allItems, not filteredItems

import { useState, useMemo } from "react";

import { Container, Stack } from "@/components/ui";

import { ModuleManager } from "../../components/ModuleManager";
import { useRoadmapContext } from "../../context/useRoadmapContext";
import { type Roadmap_Item } from "../../types/roadmap-types";
import { EditModal } from "../Overview/components/molecules/EditModal";
import { FilterBar } from "../Overview/components/molecules/FilterBar";
import { KanbanHeader, KanbanModule, KanbanStats } from "./components";

/**
 * WHAT:
 *   Main screen component that displays roadmap items in a Kanban board layout,
 *   grouped by modules and organized by status columns.
 *
 * WHY:
 *   Provides a visual workflow representation of roadmap items, making it easy
 *   to see item status distribution across different modules.
 *
 * BEHAVIOR:
 *   - Groups items by module, then by status within each module
 *   - Calculates statistics from all items (not filtered)
 *   - Manages edit modal state for item editing/creation
 *   - Handles module manager modal state
 *   - Creates new items with default values when add button clicked
 *
 * RISKS:
 *   - NOTE: crypto.randomUUID() may not be available in all browsers - add fallback
 *   - NOTE: Items without module are grouped under 'Other' - may not be intentional
 *   - NOTE: Statistics use allItems instead of filteredItems - may show incorrect counts
 *   - NOTE: New item creation uses default values - user must edit to set proper values
 *
 * @returns {JSX.Element} Die gerenderte KanbanScreen-Komponente
 *
 */

export const KanbanScreen = () => {
  // NOTE: Get all items from context
  const { items: allItems } = useRoadmapContext();
  // NOTE: State for selected item in edit modal
  const [selectedItem, setSelectedItem] = useState<null | Roadmap_Item>(null);
  // NOTE: State for edit modal visibility
  const [showEditModal, setShowEditModal] = useState(false);
  // NOTE: State for module manager modal visibility
  const [showModuleManager, setShowModuleManager] = useState(false);
  // NOTE: State flag to distinguish between create and edit mode for the modal
  const [, setIsCreateMode] = useState(false);

  /**
   * WHAT:
   *   Handles editing an existing roadmap item.
   *
   * WHY:
   *   Opens edit modal with the selected item for editing.
   *
   * BEHAVIOR:
   *   - Sets selected item
   *   - Sets create mode to false
   *   - Opens edit modal
   *
   * @param item
   *
   */
  const handleEditItem = (item: Roadmap_Item) => {
    setSelectedItem(item);
    setIsCreateMode(false);
    setShowEditModal(true);
  };

  /**
   * WHAT:
   *   Handles closing the edit modal and resetting state.
   *
   * WHY:
   *   Cleans up modal state when modal is closed.
   *
   * BEHAVIOR:
   *   - Clears selected item
   *   - Closes modal
   *   - Resets create mode
   */
  const handleCloseEditModal = () => {
    setSelectedItem(null);
    setShowEditModal(false);
    setIsCreateMode(false);
  };

  /**
   * WHAT:
   *   Creates a new roadmap item with default values and opens edit modal.
   *
   * WHY:
   *   Provides a way to create new items from the Kanban board.
   *
   * BEHAVIOR:
   *   - Creates new item with crypto.randomUUID() for ID
   *   - Sets default values (feature, open, medium priority, etc.)
   *   - Sets module to the specified module
   *   - Opens edit modal in create mode
   *
   * RISKS:
   *   - WARN: crypto.randomUUID() may not be available in all browsers - add fallback
   *   - NOTE: Empty title and description - user must fill in edit modal
   *   - NOTE: Default values may not match user's typical preferences
   *
   * @param module
   *
   */
  const handleNewItem = (module: string) => {
    const newItem: Roadmap_Item = {
      created_at: new Date().toISOString(),
      description: "",
      effort_estimate: "medium",
      id: crypto.randomUUID(),
      impact_score: "medium",
      linked_to: [],
      module: module,
      priority: "medium",
      progress: 0,
      status: "open",
      tags: [],
      title: "",
      type: "feature",
      updated_at: new Date().toISOString(),
    };
    setSelectedItem(newItem);
    setIsCreateMode(true);
    setShowEditModal(true);
  };

  // NOTE: Group items by module, then by status within each module
  const kanbanData = useMemo<Record<string, Record<string, Roadmap_Item[]>>>(() => {
    const modules: Record<string, Record<string, Roadmap_Item[]>> = {};

    for (const item of allItems) {
      const module = item.module || "Other";
      const status = item.status;

      modules[module] ??= {
        done: [],
        in_progress: [],
        open: [],
        rejected: [],
      };

      const statusArray = modules[module][status];
      if (statusArray) {
        statusArray.push(item);
      }
    }

    return modules;
  }, [allItems]);

  // NOTE: Get unique module names sorted alphabetically
  const modules = useMemo<string[]>(() => {
    const moduleNames: string[] = [];
    for (const key in kanbanData) {
      if (Object.prototype.hasOwnProperty.call(kanbanData, key)) {
        moduleNames.push(key);
      }
    }
    // Reason: toSorted() is not available in TypeScript types, but sort() mutates the array
    // We create a copy first to avoid mutation
    const sorted = [...moduleNames];
    sorted.sort((a: string, b: string) => a.localeCompare(b));
    return sorted;
  }, [kanbanData]);

  // NOTE: Calculate statistics from all items (not filtered)
  const stats = useMemo(() => {
    const totalItems = allItems.length;
    const openItems = allItems.filter((item) => item.status === "open").length;
    const inProgressItems = allItems.filter((item) => item.status === "in_progress").length;
    const doneItems = allItems.filter((item) => item.status === "done").length;
    const rejectedItems = allItems.filter((item) => item.status === "rejected").length;
    const averageProgress =
      totalItems > 0
        ? allItems.reduce((sum, item) => sum + (item.progress ?? 0), 0) / totalItems
        : 0;
    const moduleCount = modules.length;

    return {
      averageProgress,
      doneItems,
      inProgressItems,
      moduleCount,
      openItems,
      rejectedItems,
      totalItems,
    };
  }, [allItems, modules]);

  return (
    <Container className="text-white" maxWidth="full" padding="lg">
      <Stack direction="col" gap={6}>
        {/* Header */}
        <KanbanHeader
          onModuleManager={() => {
            setShowModuleManager(true);
          }}
        />

        {/* Filter Bar */}
        <FilterBar />

        {/* Statistics */}
        <KanbanStats {...stats} />

        {/* Kanban Board */}
        <div className="space-y-8">
          {modules.map((moduleName: string) => {
            const moduleData = kanbanData[moduleName];
            return moduleData ? (
              <KanbanModule
                key={moduleName}
                moduleData={moduleData}
                moduleName={moduleName}
                onAddItem={handleNewItem}
                onItemClick={handleEditItem}
              />
            ) : null;
          })}
        </div>

        {/* Empty State */}
        {modules.length === 0 && (
          <div className="text-center py-16">
            <div className="text-slate-400">
              <h3 className="text-lg font-semibold mb-2">No modules found</h3>
              <p className="text-sm">Create a module to get started with your roadmap.</p>
            </div>
          </div>
        )}

        {/* Modals */}
        {showEditModal && selectedItem ? (
          <EditModal item={selectedItem} onClose={handleCloseEditModal} />
        ) : null}

        {showModuleManager ? (
          <ModuleManager
            isOpen={showModuleManager}
            onClose={() => {
              setShowModuleManager(false);
            }}
          />
        ) : null}
      </Stack>
    </Container>
  );
};
